import React, { useContext, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

import CreateEventForm from './CreateEventForm';

import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { useLoaderDispatch } from 'context/LoaderContext';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useToast } from 'context/ToastContext';

import { addEvent, updateEvent } from 'services/Calender/calendarServices';
import { setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';
import { uploadFiles } from 'utils/filesUtils';
import { toastConstant } from 'constants/toastmessage';
import { deleteFile } from 'services/calendarServices';
import { ModalHeader } from 'shared/ModalHeader';
import { parseRemiderPayload, removeUnderScoreFields } from 'utils/utils';
import { useNavbarContext } from 'context/NavbarContext';
import momentTz from 'moment-timezone';
import { useConferenceRoomAvailability } from 'hooks/useConferenceRoomAvailability';

function CreateEventModal({ showCreateEventModal }) {
  const {
    handleSubmit,
    control,
    resetField,
    setValue,
    getValues,
    watch,
    formState: { errors },
    trigger,
  } = useForm();

  const {
    caseDetails,
    isFromCaseSpecificModule,
    isFromLeadSpecificModule,
    editEventModal,
    getEventList,
    hideCreateEventModal,
    uploadedAttachments,
    setTempDeletedAttachments,
    tempDeletedAttachments,
    eventDetails,
    setShowUserFilter,
  } = useCalendarContext();

  const { addToast } = useToast();

  const userContext = useContext(UserDetailsContext);
  const userId = userContext?.firmDetails?.current_user;
  const setLoader = useLoaderDispatch();
  const { showConfirmDialog } = useNavbarContext();
  const [isCaseUpdated, setIsCaseUpdated] = useState(false);

  // Watch form values for conference room validation
  const selectedLocation = watch('location');
  const meetingStartTime = watch('meeting_start_time');
  const meetingEndTime = watch('meeting_end_time');

  // Use the availability hook for validation only (no auto-checking)
  const { conferenceRooms } = useConferenceRoomAvailability(selectedLocation, meetingStartTime, meetingEndTime, {
    autoCheckAvailability: false, // Don't auto-check for validation purposes
    checkOnTimeChange: false, // Don't auto-check when time changes
  });

  const isEventInPast = (formData) => {
    const tz = userContext?.userDetails?.timezone;
    const now = momentTz.tz(tz);
    if (formData?.all_day) {
      const day = momentTz.tz(formData?.when || formData?.meeting_start_time, tz).startOf('day');
      return day.isBefore(now.startOf('day'));
    }
    const start = momentTz.tz(formData?.meeting_start_time || formData?.when, tz);
    return start.isBefore(now);
  };

  const handleEditRecurringEvent = async (data) => {
    // Block updating past events (including all-day)
    if (editEventModal && isEventInPast(data)) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Past events cannot be edited.');
      return;
    }
    // Validate conference room availability before proceeding
    if (data.conference_room_id) {
      const selectedRoom = conferenceRooms.find((room) => room.value === data.conference_room_id);

      // Only block if room is confirmed unavailable
      if (selectedRoom && selectedRoom.hasAvailabilityData && selectedRoom.isAvailable === false) {
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          'Conference room is already booked for the selected time slot. Please select a different room or time.'
        );
        return;
      }
    }

    if (eventDetails?.is_recurring && data?.is_recurring) {
      const result = await showConfirmDialog({
        footer: {
          cancelLable: 'Cancel',
          okLable: 'Update',
        },
        formFields: [
          {
            type: 'title',
            label: 'Update Event',
          },
          {
            name: 'selection',
            type: 'radiobutton',
            label: 'Option',
            required: true,
            categories: [
              { key: 'current', label: 'This event' },
              { key: 'futures', label: 'This and following events' },
              { key: 'all', label: 'All recurring events' },
            ],
          },
        ],
      })?.catch((error) => console.error('Dialog closed or error:', error));
      if (!result) return;
      const editAll = result?.selection;
      saveCreateEvent(data, editAll);
    } else {
      saveCreateEvent(data);
    }
  };

  const saveCreateEvent = async (formData, editAll = 'current') => {
    let data = { ...formData };

    // Only validate conference room availability if we have the data
    // Don't block saving if availability data isn't available
    if (data.conference_room_id) {
      const selectedRoom = conferenceRooms.find((room) => room.value === data.conference_room_id);

      // Only block if room is confirmed unavailable
      if (selectedRoom && selectedRoom.hasAvailabilityData && selectedRoom.isAvailable === false) {
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          'Conference room is already booked for the selected time slot. Please select a different room or time.'
        );
        return;
      }
    }

    if (tempDeletedAttachments.length > 0 && editEventModal) {
      await Promise.all(tempDeletedAttachments.map((item) => deleteFileAsync(item)));
      setTempDeletedAttachments([]);
    }

    if (data?.attachments?.length > 0) {
      try {
        setLoader(true);
        setShowUserFilter(false);
        const caseIdForUpload = isFromCaseSpecificModule || isFromLeadSpecificModule ? caseDetails?.case_id : data.case?.id;
        const res = await uploadFiles(data?.attachments, 'calendar', caseIdForUpload, userId);
        setLoader(false);
        data.attachments = res;
      } catch (err) {
        data.attachments = [];
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err.message);
        setLoader(false);
        console.log(err);
        return;
      }
    }

    // reminders
    data.reminder = parseRemiderPayload(data.reminder);

    data.reminder = Array.from(new Set(data?.reminder));

    if (data.all_day && data.when) {
      const whenDate = new Date(data.when);
      const startTime = new Date(whenDate);
      startTime.setHours(0, 0, 0, 0);

      const endReference = data.till ? new Date(data.till) : new Date(whenDate);
      const endTime = new Date(endReference);
      endTime.setHours(23, 59, 59, 999);

      data.meeting_start_time = startTime;
      data.meeting_end_time = endTime;
    } else if (!data.all_day) {
      if (data.meeting_start_time) {
        data.meeting_start_time = new Date(data.meeting_start_time);
      }
      if (data.meeting_end_time) {
        data.meeting_end_time = new Date(data.meeting_end_time);
      }
    }

    // start and end time to timezone format
    data.meeting_start_time = setTimezoneWithoutAlteringTime(data.meeting_start_time, userContext?.userDetails?.timezone);
    data.meeting_end_time = setTimezoneWithoutAlteringTime(data.meeting_end_time, userContext?.userDetails?.timezone);
    data.when = setTimezoneWithoutAlteringTime(data.meeting_start_time, userContext?.userDetails?.timezone);
    if (data.till) {
      data.till = setTimezoneWithoutAlteringTime(data.meeting_end_time, userContext?.userDetails?.timezone);
    }
    // case name
    data.case_name =
      isFromCaseSpecificModule || isFromLeadSpecificModule ? caseDetails?.case_description?.case_name : data.case?.case_name ?? '';

    data.case_id = isFromCaseSpecificModule || isFromLeadSpecificModule ? caseDetails?.case_id : data.case?.id;
    data.is_lead = isFromLeadSpecificModule ? isFromLeadSpecificModule : data?.case?.is_lead;
    // icon
    data.icon = data.event_type?.event_type_icon ?? '';

    data.practice_area = caseDetails?.case_description?.case_practice_area || data?.case?.practice_area || data?.practice_area;
    data = await appendRemainingItems(data, eventDetails);

    if (!(editEventModal && eventDetails)) {
      data.guests = [...data.contacts, ...data.firm_users];
    }
    data.recurrence_keys = data.recurrence_keys ?? {};

    // map firm-wide toggle to API field
    if (typeof data.firm_wide !== 'undefined') {
      data.is_firm_event = Boolean(data.firm_wide);
    }
    const isFirmEvent = data.firm_wide === true || data.is_firm_event === true;
    // When firm-wide event is ON, ensure guests and selected_attendees_list are empty and clear case
    if (isFirmEvent) {
      data.guests = [];
      data.selected_attendees_list = [];
      // remove case fields from payload for firm-wide events
      try {
        delete data.case;
      } catch (e) {}
      try {
        delete data.case_id;
      } catch (e) {}
      data.case_name = undefined;
      data.is_lead = false;
    }
    if (data.is_recurring) {
      data.recurrence_keys.recurrence_limit = setTimezoneWithoutAlteringTime(
        data.recurrence_keys.recurrence_limit,
        userContext?.userDetails?.timezone
      );
    }

    if (editEventModal) {
      let previousAttachments = Array.isArray(uploadedAttachments) ? uploadedAttachments : [];
      let currentAttachments = Array.isArray(data?.attachments) ? data?.attachments : [];

      const concatenatedArray = [...previousAttachments, ...currentAttachments];
      data.attachments = concatenatedArray;

      data.accessible_list.push(eventDetails?.created_by);

      let month = new Date(data.when).getMonth() + 1;
      let year = new Date(data.when).getFullYear();

      data.event_month = month;
      data.event_year = year;

      let reminderUpdate = checkReminderUpdated(data.reminder, eventDetails?.reminder);
      if (data?.groups?.length > 0) {
        data.groups = data.groups?.map((group) => {
          const { firm_user_data, ...groupWithoutFirmUserData } = group;
          return groupWithoutFirmUserData;
        });
      }

      updateEvent({
        data: removeUnderScoreFields({
          ...data,
          is_case_updated: isCaseUpdated,
        }),
        case_id: isFirmEvent ? '' : isFromCaseSpecificModule || isFromLeadSpecificModule ? caseDetails?.case_id : data.case?.id,
        is_lead: isFirmEvent ? false : isFromLeadSpecificModule ? isFromLeadSpecificModule : data?.case?.is_lead,
        reminder_updated: reminderUpdate,
        send_update_notification: true,
        recurrence_scope: editAll,
        overriden_start_date: eventDetails?.meeting_start_time || '',
        isPrevEventRecurring: (eventDetails?.is_recurring && data?.is_recurring) || false,
        is_date_changed: !momentTz(data.when).isSame(eventDetails?.when, 'day') || false,
      })
        .then((res) => {
          hideCreateEventModal();
          getEventList();
          setIsCaseUpdated(false); // <-- reset after success
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.EVENT_UPDATE_SUCCESS);
        })
        .catch((err) => {
          console.log(err);

          let msg = toastConstant.message.EVENT_UPDATE_FAILURE;
          if (err && err.response && err.response.data) msg = err.response.data;
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, msg);
        });
    } else {
      if (!isFirmEvent && (isFromCaseSpecificModule || isFromLeadSpecificModule)) {
        data.case = {
          id: caseDetails?.case_id,
          value: caseDetails?.case_description?.case_name,
          label:
            caseDetails.case_description && caseDetails.case_description.case_name
              ? `${caseDetails.unique_number ? `${caseDetails.unique_number} ` : ''}${caseDetails.case_description.case_name}`
              : 'TBD',
        };
      }

      addEvent({
        data: removeUnderScoreFields(data),
        case_id: isFirmEvent ? '' : isFromCaseSpecificModule || isFromLeadSpecificModule ? caseDetails?.case_id : data.case?.id,
        is_lead: isFirmEvent ? false : isFromLeadSpecificModule ? isFromLeadSpecificModule : data?.case?.is_lead,
      })
        .then((res) => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.EVENT_CREATED_SUCCESS);
          hideCreateEventModal();
          getEventList();
        })
        .catch((err) => {
          console.log(err);

          let msg = toastConstant.message.EVENT_CREATE_FAILURE;
          if (err && err.response && err.response.data) msg = err.response.data;
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, msg);
        });
    }
  };

  async function deleteFileAsync(file) {
    try {
      await deleteFile(file);
    } catch (err) {
      console.error(err);
    }
  }

  const appendRemainingItems = (objA, objB) => {
    for (let key in objB) {
      if (!objA.hasOwnProperty(key)) {
        objA[key] = objB[key];
      }
    }
    return objA;
  };

  const checkReminderUpdated = (arr1, arr2) => {
    if (arr1?.length !== arr2?.length) {
      return true;
    }
    return arr1?.every((element, index) => element !== arr2[index]);
  };

  return (
    <Dialog
      visible={showCreateEventModal}
      modal
      onHide={hideCreateEventModal}
      className="new-task-popup media-popup"
      resizable={false}
      header={ModalHeader}
      footer={() => (
        <div className="footer-btn">
          <Button label="Cancel" className="btn p-button-secondary outline me-2" onClick={hideCreateEventModal} />
          <Button
            label={editEventModal ? 'Update' : 'Save'}
            type="button"
            className="p-button-secondary"
            onClick={handleSubmit(handleEditRecurringEvent)}
          />
        </div>
      )}
    >
      <CreateEventForm
        onHide={hideCreateEventModal}
        saveCreateEvent={saveCreateEvent}
        control={control}
        resetField={resetField}
        setValue={setValue}
        getValues={getValues}
        watch={watch}
        errors={errors}
        trigger={trigger}
        setIsCaseUpdated={setIsCaseUpdated}
        caseDetails={caseDetails}
      />
    </Dialog>
  );
}

export default CreateEventModal;
